import { useAuth } from "@/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { APP_LOGO, APP_TITLE, APP_DESCRIPTION } from "@shared/const";
import { AlertCircle, Loader2 } from "lucide-react";

/**
 * MyPA Home Page
 * Landing page for the carbon accounting platform
 */
export default function Home() {
  let { user, loading, error, isAuthenticated, signOut } = useAuth();
  const [, setLocation] = useLocation();
  
  const { data: profile, isLoading: profileLoading } = trpc.profile.get.useQuery(
    undefined,
    { enabled: isAuthenticated }
  );
  
  // Redirect logic based on profile status (in useEffect to avoid render-phase side effects)
  useEffect(() => {
    if (isAuthenticated && !profileLoading) {
      if (!profile) {
        setLocation("/profile/complete");
      } else if (!profile.is_locked) {
        // Only redirect to dashboard if profile is complete and not locked
        setLocation("/dashboard");
      }
    }
  }, [isAuthenticated, profileLoading, profile, setLocation]);
  // If theme is switchable in App.tsx, we can implement theme toggling like this:
  // const { theme, toggleTheme } = useTheme();

  const LoginBoxContent = () => {
    return (
      <>
          <CardHeader className="text-center">
            {isAuthenticated ? (
              <>
                <CardTitle className="text-2xl">Welcome back</CardTitle>
                <CardDescription>{APP_DESCRIPTION}</CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-2xl">Get Started with MyPA</CardTitle>
                <CardDescription>{APP_DESCRIPTION}</CardDescription>
              </>
            )}
          </CardHeader>

        <Separator />

        <CardContent className="flex justify-center">
          {isAuthenticated ? (
            <div className="flex items-center gap-4">
              <Avatar className="size-12">
                <AvatarFallback className="text-lg font-semibold">
                  {(user?.firstName || user?.email)?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="font-semibold">{user?.firstName || user?.email}</div>
                <div className="text-sm text-muted-foreground">
                  {user?.email}
                </div>
              </div>
            </div>
          ) : (
            <Button
              className="w-full bg-gradient-mypa hover:opacity-90"
              size="lg"
              onClick={() => setLocation("/login")}
            >
              Sign In to MyPA
            </Button>
          )}
        </CardContent>
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="w-full border-b px-4 flex items-center justify-between h-16 bg-gradient-mypa">
        <div className="flex items-center gap-3">
          <img
            src={APP_LOGO}
            alt="MyPA Logo"
            className="h-10 w-10 rounded-lg object-contain"
          />
          <span className="text-xl font-bold text-white">{APP_TITLE}</span>
        </div>

        <div className="flex items-center gap-2">
          {isAuthenticated && (
            <Button 
              variant="outline" 
              onClick={signOut}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              Sign Out
            </Button>
          )}
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6 bg-gradient-mypa-light">
        <Card style={{ minWidth: "350px" }}>
          {loading ? (
            <div className="flex items-center justify-center p-4">
              {/* Show loading at component level, not page level - keeps UI interactive */}
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <LoginBoxContent />
          )}
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
      </main>
    </div>
  );
}
